/**
 * Early Check-Out Dialog Component
 * Allows front desk to schedule early checkout with refund calculation
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { CalendarX, Info, AlertTriangle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export const EarlyCheckOutDialog = ({
  open,
  onOpenChange,
  guest,
  onConfirm,
  loading
}) => {
  const [newCheckOutDate, setNewCheckOutDate] = useState('');
  const [showRefund, setShowRefund] = useState(true);
  
  const handleSubmit = () => {
    if (!newCheckOutDate) {
      toast.error('Please select a new check-out date');
      return;
    }
    
    // Validate that new date is before current check-out date
    const currentCheckOut = new Date(guest?.check_out);
    const newDate = new Date(newCheckOutDate);
    
    if (newDate >= currentCheckOut) {
      toast.error('New check-out date must be before current check-out date');
      return;
    }
    
    // Validate that new date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    newDate.setHours(0, 0, 0, 0);
    
    if (newDate < today) {
      toast.error('Check-out date cannot be in the past');
      return;
    }
    
    onConfirm(newCheckOutDate, showRefund);
  };
  
  const calculateRefundDetails = () => {
    if (!newCheckOutDate || !guest?.check_out || !guest?.check_in) {
      return { nightsLost: 0, refundAmount: 0, nightsStayed: 0, totalNights: 0 };
    }
    
    const checkIn = new Date(guest.check_in);
    const originalCheckOut = new Date(guest.check_out);
    const newCheckOut = new Date(newCheckOutDate);
    
    // Calculate total nights originally booked
    const totalNights = Math.ceil((originalCheckOut - checkIn) / (1000 * 60 * 60 * 24));
    
    // Calculate nights actually stayed
    const nightsStayed = Math.ceil((newCheckOut - checkIn) / (1000 * 60 * 60 * 24));
    
    // Calculate nights lost
    const nightsLost = totalNights - nightsStayed;
    
    // Calculate refund (assuming equal nightly rate)
    const totalPrice = guest.final_price || guest.room_rate * totalNights || 0;
    const nightlyRate = totalNights > 0 ? totalPrice / totalNights : 0;
    const refundAmount = nightsLost * nightlyRate;
    
    return { 
      nightsLost: Math.max(0, nightsLost), 
      refundAmount: Math.max(0, refundAmount),
      nightsStayed: Math.max(0, nightsStayed),
      totalNights,
      nightlyRate
    };
  };
  
  const refundDetails = calculateRefundDetails();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="early-checkout-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-red-500" />
            Early Check-Out
          </DialogTitle>
        </DialogHeader>

        {guest && (
          <div className="space-y-4">
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">{guest.guest_name}</p>
                <div className="space-y-1 text-sm">
                  <p>Current Check-Out: <span className="font-semibold">{guest.check_out}</span></p>
                  <p>Room: {guest.room_type || guest.assigned_room_number || 'N/A'}</p>
                </div>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="new-early-checkout-date">New Check-Out Date</Label>
              <Input
                id="new-early-checkout-date"
                type="date"
                value={newCheckOutDate}
                onChange={(e) => setNewCheckOutDate(e.target.value)}
                max={guest.check_out}
                data-testid="new-early-checkout-date-input"
              />
              <p className="text-xs text-muted-foreground">
                Select a date before {guest.check_out}
              </p>
            </div>

            {refundDetails.nightsLost > 0 && (
              <>
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <AlertDescription className="text-orange-900">
                    <p className="font-semibold mb-2">
                      -{refundDetails.nightsLost} {refundDetails.nightsLost === 1 ? 'night' : 'nights'} (Early Departure)
                    </p>
                    <div className="text-sm space-y-1">
                      <p>Originally booked: {refundDetails.totalNights} nights</p>
                      <p>Nights stayed: {refundDetails.nightsStayed} nights</p>
                      <p>Unused nights: {refundDetails.nightsLost} nights</p>
                    </div>
                  </AlertDescription>
                </Alert>
                
                {showRefund && (
                  <Alert className="bg-green-50 border-green-200">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <AlertDescription className="text-green-900">
                      <p className="font-semibold mb-1">Refund Calculation</p>
                      <div className="text-sm space-y-1">
                        <p>Nightly rate: ${refundDetails.nightlyRate.toFixed(2)}</p>
                        <p>Unused nights: {refundDetails.nightsLost}</p>
                        <p className="font-bold text-lg mt-2">
                          Refund Amount: ${refundDetails.refundAmount.toFixed(2)}
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="show-refund"
                    checked={showRefund}
                    onChange={(e) => setShowRefund(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="show-refund" className="text-sm cursor-pointer">
                    Process refund for unused nights
                  </label>
                </div>
              </>
            )}
            
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800 text-sm">
                <p className="font-semibold mb-1">After confirmation:</p>
                <p>• Guest will be moved to Departures Today</p>
                <p>• Front desk can process final checkout</p>
                {showRefund && refundDetails.refundAmount > 0 && (
                  <p>• Refund will be shown in checkout folio</p>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!newCheckOutDate || loading}
            variant="destructive"
            data-testid="confirm-early-checkout-btn"
          >
            {loading ? 'Processing...' : 'Schedule Early Check-Out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
